import React, { useCallback, useEffect, useId, useRef, useState } from "react";

const clampGridSize = (value, fallback) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return fallback;
  return Math.min(4, Math.max(2, Math.round(number)));
};

const shuffle = (items) => {
  const result = [...items];
  for (let index = result.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(Math.random() * (index + 1));
    [result[index], result[randomIndex]] = [result[randomIndex], result[index]];
  }
  return result;
};

const getHorizontalEdgeSign = (row, column) => ((row + column) % 2 === 0 ? 1 : -1);
const getVerticalEdgeSign = (row, column) => ((row + column) % 2 === 0 ? -1 : 1);

const getPieceEdges = (pieceIndex, rows, columns) => {
  const row = Math.floor(pieceIndex / columns);
  const column = pieceIndex % columns;

  return {
    top: row === 0 ? 0 : -getHorizontalEdgeSign(row - 1, column),
    right: column === columns - 1 ? 0 : getVerticalEdgeSign(row, column),
    bottom: row === rows - 1 ? 0 : getHorizontalEdgeSign(row, column),
    left: column === 0 ? 0 : -getVerticalEdgeSign(row, column - 1),
  };
};

const createPiecePath = ({ top, right, bottom, left }) => {
  const knob = 12;
  const neckA = 36;
  const neckB = 64;
  const topY = -top * knob;
  const rightX = 100 + right * knob;
  const bottomY = 100 + bottom * knob;
  const leftX = -left * knob;

  return [
    "M 0 0",
    top
      ? `L ${neckA} 0 C ${neckA + 3} ${topY} ${neckB - 3} ${topY} ${neckB} 0`
      : "L 100 0",
    top ? "L 100 0" : "",
    right
      ? `L 100 ${neckA} C ${rightX} ${neckA + 3} ${rightX} ${neckB - 3} 100 ${neckB}`
      : "L 100 100",
    right ? "L 100 100" : "",
    bottom
      ? `L ${neckB} 100 C ${neckB - 3} ${bottomY} ${neckA + 3} ${bottomY} ${neckA} 100`
      : "L 0 100",
    bottom ? "L 0 100" : "",
    left
      ? `L 0 ${neckB} C ${leftX} ${neckB - 3} ${leftX} ${neckA + 3} 0 ${neckA}`
      : "L 0 0",
    left ? "L 0 0" : "",
    "Z",
  ].filter(Boolean).join(" ");
};

const createScatteredPositions = (canvasRect, pieceSize, pieceCount) => {
  const gap = Math.max(8, pieceSize * 0.12);
  const candidates = [];

  for (let y = gap; y <= canvasRect.height - pieceSize - gap; y += pieceSize + gap) {
    for (let x = gap; x <= canvasRect.width - pieceSize - gap; x += pieceSize + gap) {
      candidates.push({ x, y });
    }
  }

  const shuffled = shuffle(candidates);
  return Array.from({ length: pieceCount }, (_, index) => ({
    ...(shuffled[index] || {
      x: gap + (index % 3) * gap,
      y: gap + (index % 4) * gap,
    }),
    groupId: index,
  }));
};

const JigsawPiece = ({
  pieceIndex,
  rows,
  columns,
  imgPath,
  position,
  pieceSize,
  solved,
  isActive,
  onStart,
  onMove,
  onRelease,
}) => {
  const instanceId = useId().replace(/:/g, "");
  const dragOffset = useRef({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const row = Math.floor(pieceIndex / columns);
  const column = pieceIndex % columns;
  const path = createPiecePath(getPieceEdges(pieceIndex, rows, columns));
  const clipId = `jigsaw-clip-${instanceId}-${pieceIndex}`;

  const startDragging = (event) => {
    if (solved) return;
    const pieceRect = event.currentTarget.getBoundingClientRect();
    dragOffset.current = {
      x: event.clientX - pieceRect.left,
      y: event.clientY - pieceRect.top,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    setDragging(true);
    onStart(pieceIndex);
  };

  const movePiece = (event) => {
    if (!dragging) return;
    onMove(pieceIndex, event.clientX, event.clientY, dragOffset.current);
  };

  const stopDragging = (event) => {
    if (!dragging) return;
    setDragging(false);
    onRelease(pieceIndex, event.clientX, event.clientY, dragOffset.current);
  };

  return (
    <button
      type="button"
      className={[
        "jigsaw-free-piece",
        isActive ? "is-active" : "",
        dragging ? "is-dragging" : "",
      ].filter(Boolean).join(" ")}
      style={{
        left: position.x,
        top: position.y,
        width: pieceSize,
        height: pieceSize,
      }}
      onPointerDown={startDragging}
      onPointerMove={movePiece}
      onPointerUp={stopDragging}
      onPointerCancel={stopDragging}
      disabled={solved}
      aria-label={`Деталь пазла ${pieceIndex + 1}`}
    >
      <svg
        className="jigsaw-piece-svg"
        viewBox="-14 -14 128 128"
        aria-hidden="true"
        focusable="false"
        preserveAspectRatio="none"
      >
        <defs>
          <clipPath id={clipId} clipPathUnits="userSpaceOnUse">
            <path d={path} />
          </clipPath>
        </defs>
        <image
          href={imgPath}
          x={-column * 100}
          y={-row * 100}
          width={columns * 100}
          height={rows * 100}
          preserveAspectRatio="none"
          clipPath={`url(#${clipId})`}
        />
        <path className="jigsaw-piece-edge" d={path} />
      </svg>
    </button>
  );
};

const JigsawQuestion = ({
  question,
  imgPath,
  rows,
  columns,
  showPreview,
  onSolved,
  showNextButton,
  onNext,
  showFinishButton,
  onFinish,
}) => {
  const safeRows = clampGridSize(rows, 2);
  const safeColumns = clampGridSize(columns, 2);
  const pieceCount = safeRows * safeColumns;
  const canvasRef = useRef(null);
  const solvedRef = useRef(false);
  const [pieceSize, setPieceSize] = useState(80);
  const [positions, setPositions] = useState([]);
  const [activeGroup, setActiveGroup] = useState(null);

  const resetJigsaw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const canvasRect = canvas.getBoundingClientRect();
    const nextPieceSize = Math.min(130, 460 / safeColumns, canvasRect.width / (safeColumns + 1));
    setPieceSize(nextPieceSize);
    setPositions(createScatteredPositions(canvasRect, nextPieceSize, pieceCount));
    setActiveGroup(null);
    solvedRef.current = false;
  }, [pieceCount, safeColumns]);

  useEffect(() => {
    const frame = window.requestAnimationFrame(resetJigsaw);
    return () => window.cancelAnimationFrame(frame);
  }, [imgPath, resetJigsaw]);

  const groupSizes = positions.reduce((sizes, position) => {
    sizes[position.groupId] = (sizes[position.groupId] || 0) + 1;
    return sizes;
  }, {});
  const groupCount = Object.keys(groupSizes).length;
  const connectedCount = Math.max(0, ...Object.values(groupSizes));
  const solved = positions.length === pieceCount && groupCount === 1;

  useEffect(() => {
    if (!solved || solvedRef.current) return;
    solvedRef.current = true;
    onSolved?.();
  }, [solved, onSolved]);

  const getPointerPosition = useCallback((clientX, clientY, offset) => {
    const canvasRect = canvasRef.current.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(canvasRect.width - pieceSize, clientX - canvasRect.left - offset.x)),
      y: Math.max(0, Math.min(canvasRect.height - pieceSize, clientY - canvasRect.top - offset.y)),
    };
  }, [pieceSize]);

  const movePiece = useCallback((pieceIndex, clientX, clientY, offset) => {
    const nextPosition = getPointerPosition(clientX, clientY, offset);
    setPositions((current) => {
      const draggedPiece = current[pieceIndex];
      const deltaX = nextPosition.x - draggedPiece.x;
      const deltaY = nextPosition.y - draggedPiece.y;

      return current.map((position) =>
        position.groupId === draggedPiece.groupId
          ? { ...position, x: position.x + deltaX, y: position.y + deltaY }
          : position
      );
    });
  }, [getPointerPosition]);

  const releasePiece = useCallback((pieceIndex) => {
    setPositions((current) => {
      let next = current.map((position) => ({ ...position }));
      let movingGroupId = next[pieceIndex].groupId;
      let merged = true;

      while (merged) {
        merged = false;

        for (let firstIndex = 0; firstIndex < pieceCount && !merged; firstIndex += 1) {
          if (next[firstIndex].groupId !== movingGroupId) continue;

          const firstRow = Math.floor(firstIndex / safeColumns);
          const firstColumn = firstIndex % safeColumns;

          for (let secondIndex = 0; secondIndex < pieceCount; secondIndex += 1) {
            if (next[secondIndex].groupId === movingGroupId) continue;

            const secondRow = Math.floor(secondIndex / safeColumns);
            const secondColumn = secondIndex % safeColumns;
            const areNeighbours =
              Math.abs(firstRow - secondRow) + Math.abs(firstColumn - secondColumn) === 1;
            if (!areNeighbours) continue;

            const expectedX = next[firstIndex].x + (secondColumn - firstColumn) * pieceSize;
            const expectedY = next[firstIndex].y + (secondRow - firstRow) * pieceSize;
            const snapX = next[secondIndex].x - expectedX;
            const snapY = next[secondIndex].y - expectedY;

            if (Math.hypot(snapX, snapY) > pieceSize * 0.38) continue;

            const sourceGroupId = movingGroupId;
            const targetGroupId = next[secondIndex].groupId;
            next = next.map((position) =>
              position.groupId === sourceGroupId
                ? {
                    ...position,
                    x: position.x + snapX,
                    y: position.y + snapY,
                    groupId: targetGroupId,
                  }
                : position
            );
            movingGroupId = targetGroupId;
            merged = true;
            break;
          }
        }
      }

      return next;
    });
    setActiveGroup(null);
  }, [pieceCount, pieceSize, safeColumns]);

  if (!imgPath) {
    return <div className="jigsaw-empty">Для пазла потрібно додати зображення в адмінці.</div>;
  }

  return (
    <div className="jigsaw-question">
      {String(question || "").trim() && (
        <div className="test-question question-title" dangerouslySetInnerHTML={{ __html: question }} />
      )}

      <p className="jigsaw-instruction">З’єднуй сусідні деталі між собою. Зібрані частини рухаються разом.</p>

      <div className="jigsaw-layout">
        <div className="jigsaw-canvas" ref={canvasRef}>
          {positions.map((position, pieceIndex) => (
            <JigsawPiece
              key={pieceIndex}
              pieceIndex={pieceIndex}
              rows={safeRows}
              columns={safeColumns}
              imgPath={imgPath}
              position={position}
              pieceSize={pieceSize}
              solved={solved}
              isActive={activeGroup === position.groupId}
              onStart={() => setActiveGroup(position.groupId)}
              onMove={movePiece}
              onRelease={releasePiece}
            />
          ))}
        </div>

        {showPreview !== false && (
          <aside className="jigsaw-preview">
            <span>Зразок</span>
            <img src={imgPath} alt="Зразок готового пазла" />
          </aside>
        )}
      </div>

      <div className="jigsaw-actions">
        <button type="button" className="jigsaw-reset" onClick={resetJigsaw}>Перемішати</button>
        <div className={`jigsaw-progress${solved ? " is-solved" : ""}`} aria-live="polite">
          {solved ? "Пазл складено!" : `Найбільша частина: ${connectedCount}/${pieceCount}`}
        </div>
      </div>

      {solved && showNextButton && (
        <button className="btn nextAnswer" onClick={onNext} style={{ display: "inline-block" }}>
          Наступне завдання
        </button>
      )}

      {solved && showFinishButton && (
        <button className="btn nextAnswer" onClick={onFinish} style={{ display: "inline-block" }}>
          Завершити
        </button>
      )}
    </div>
  );
};

export default JigsawQuestion;
